import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Permissions } from '../../auth/permissions'
import { useSession } from '../../auth/session'
import { Button, ErrorBanner, Modal } from '../../components/forms'
import { formatDateTime, formatMoneyDocument, formatPhone } from '../../lib/format'
import { tenderLabels, useReceipt, type Receipt } from '../../features/pos/api'
import { openPdf, useInvoice, useSendReceipt } from '../../features/messaging/api'
import { MessageLine, SendDocument, WhatsAppOnDevice } from '../sales/shared'

/**
 * The receipt after a sale, ready to print on an 80 mm roll.
 *
 * Printed from the browser: a print stylesheet hides everything but the receipt
 * while this dialogue prints, and the page's other print uses (list exports) are
 * untouched because the rule only applies while the receipt class is set.
 *
 * Beneath it, what was sent automatically (M16) — the SMS thank-you with its link, and
 * whether it arrived — and the receipt by WhatsApp or email when the customer asks.
 */
/** D-144: only a VAT-registered seller issues a tax invoice. Matches the PDF receipt. */
function receiptTitle(r: { saleType: string; sellerVatRegistered?: boolean }): string {
  const registered = r.sellerVatRegistered !== false
  if (r.saleType === 'Credit') return registered ? 'TAX INVOICE — ON ACCOUNT' : 'INVOICE — ON ACCOUNT'
  return registered ? 'TAX INVOICE / RECEIPT' : 'RECEIPT'
}

export function ReceiptDialog({ invoiceId, pointsEarned = 0, onClose }: { invoiceId: string; pointsEarned?: number; onClose: () => void }) {
  const navigate = useNavigate()
  const can = useSession((s) => s.can)
  const receipt = useReceipt(invoiceId)
  const invoice = useInvoice(invoiceId)
  const send = useSendReceipt(invoiceId)
  const [pdfError, setPdfError] = useState<unknown>(null)

  /**
   * Prints the receipt as ONE page, cut to the length of the sale (D-229).
   *
   * Without a page size the browser prints onto whatever the printer defaults to, usually A4, and
   * a long sale runs off the bottom of one sheet onto the next — one receipt arriving as two, three
   * or four, which is what a shop reported after the repeat in D-225 was already fixed.
   *
   * `size: 72mm auto` does NOT solve it: Chromium keeps the default page height and still breaks.
   * Measured against a forty-line receipt it paginated exactly as A4 did. A fixed tall page prints
   * as one but feeds the roll to its full declared length, so a three-metre page wastes three
   * metres of paper.
   *
   * So the height is measured and set, which is what the server's PDF has always done: one page,
   * exactly as long as the sale. Injected for the duration of the print because `@page` cannot be
   * scoped by a class — left in the stylesheet it would decide the paper for every report in the
   * app.
   */
  function print() {
    const root = document.documentElement

    const page = document.createElement('style')
    page.textContent = `@page { size: 72mm ${receiptHeightMm()}mm; margin: 4mm; }`

    document.head.appendChild(page)
    root.classList.add('printing-receipt')

    const done = () => {
      root.classList.remove('printing-receipt')
      page.remove()
      window.removeEventListener('afterprint', done)
    }

    window.addEventListener('afterprint', done)
    window.print()
  }

  return (
    <Modal
      open
      title={receipt.data ? `Sale complete — ${receipt.data.orderNumber ?? receipt.data.invoiceCode}` : 'Sale complete'}
      onClose={onClose}
      footer={
        <>
          <Button tone="secondary" onClick={print} disabled={!receipt.data}>
            Print receipt
          </Button>
          <Button
            tone="secondary"
            disabled={!receipt.data}
            onClick={() => {
              setPdfError(null)
              openPdf({ kind: 'receipt', id: invoiceId }).catch(setPdfError)
            }}
          >
            PDF
          </Button>
          {can(Permissions.Delivery.Create) && (
            <Button tone="secondary" onClick={() => { onClose(); navigate(`/logistics/new?invoice=${invoiceId}`) }}>
              Deliver this
            </Button>
          )}
          <Button onClick={onClose} autoFocus>
            New sale
          </Button>
        </>
      }
    >
      <ErrorBanner error={receipt.error} />
      {receipt.data ? (
        <>
          {receipt.data.changeGiven > 0 && (
            <p className="mb-4 rounded-md bg-dpl-success/10 px-3 py-3 text-center text-lg font-semibold text-dpl-success" role="status">
              Give change: {formatMoneyDocument(receipt.data.changeGiven)}
            </p>
          )}
          {pointsEarned > 0 && (
            <p className="mb-3 text-center text-sm text-dpl-success" role="status">
              {pointsEarned.toLocaleString('en-KE')} loyalty point{pointsEarned === 1 ? '' : 's'} earned.
            </p>
          )}
          <div className="mx-auto max-h-[45vh] overflow-y-auto rounded border border-dpl-border bg-white p-3">
            <ReceiptView receipt={invoice.data?.receipt ?? receipt.data} />
          </div>

          {/* The copy the printer gets, rendered as a direct child of <body> (D-225).
              Printing it from inside the dialogue produced one receipt per page of the till screen
              behind it — three, usually. A rule can only hide "everything but this" when "this" has
              no hidden ancestors, and the dialogue is nested deep inside #root. Invisible on screen;
              the print stylesheet is the only thing that ever shows it. */}
          {createPortal(
            <div className="receipt-print-root">
              <ReceiptView receipt={invoice.data?.receipt ?? receipt.data} />
            </div>,
            document.body,
          )}
          <div className="mt-4 space-y-2">
            {(invoice.data?.messages.length ?? 0) > 0 && (
              <ul className="divide-y divide-dpl-border/60" aria-label="Receipt sent">
                {invoice.data!.messages.map((m) => <MessageLine key={m.id} message={m} />)}
              </ul>
            )}
            <SendDocument
              channels={['WhatsApp', 'Email', 'Sms']}
              // The REAL number, not the printed one: masking is about the paper, not about
              // stopping the till texting a customer their own receipt (D-246).
              defaults={{
                Sms: receipt.data.customerPhoneForSending ?? receipt.data.customerPhone,
                WhatsApp: receipt.data.customerPhoneForSending ?? receipt.data.customerPhone,
                Email: receipt.data.customerEmail,
              }}
              busy={send.isPending}
              error={send.error ?? pdfError}
              onSend={(channel, recipient) => send.mutate({ channel, recipient })}
            />
            <WhatsAppOnDevice invoiceId={invoiceId} phone={receipt.data.customerPhoneForSending ?? receipt.data.customerPhone} />
          </div>
        </>
      ) : (
        <div className="h-40 animate-pulse rounded bg-dpl-bg" aria-busy="true" />
      )}
    </Modal>
  )
}

const rate = (r: number) => `${Number.isInteger(r) ? r : r.toFixed(2)}%`

/** DP Light's receipt mockup (D-135): what the printer and the PDF show, on the screen too. */
const receiptTagline = 'BRIGHTER SOLUTIONS FOR A BETTER TOMORROW'
const receiptSignOff = 'QUALITY PRODUCTS | GREAT SERVICE | BRIGHTER SPACES'
/**
 * How tall the printed receipt is, in millimetres (D-236).
 *
 * <b>It has to be laid out to be measured.</b> The printable copy is `display: none` on screen —
 * that is the whole point of it — and a display:none element has no boxes at all, so scrollHeight
 * is 0. Measuring it where it sits asked for a 10mm page and chopped every receipt into slivers.
 *
 * So it is shown off-screen for the length of one measurement and put back. Reading the on-screen
 * preview instead would measure a different element: that one sits in a scrolling box and carries
 * the dialogue's padding, neither of which is printed.
 */
function receiptHeightMm(): number {
  const area = document.querySelector<HTMLElement>('.receipt-print-root')

  if (!area) {
    return 300
  }

  const before = area.getAttribute('style')
  area.setAttribute('style', 'display:block;position:absolute;left:-10000px;top:0;width:72mm')

  // 96 CSS pixels to the inch, plus the 4mm margins that @page's size includes.
  const height = Math.ceil((area.scrollHeight * 25.4) / 96) + 10

  if (before === null) {
    area.removeAttribute('style')
  } else {
    area.setAttribute('style', before)
  }

  // Nothing measurable is a measurement that failed, not a receipt with no lines on it. A tall
  // page wastes a little roll; a 10mm one destroys the receipt.
  return height > 20 ? height : 300
}

const bare = (amount: number) => formatMoneyDocument(amount).replace('KSh ', '')

export function ReceiptView({ receipt: r }: { receipt: Receipt }) {
  const mixedRates = new Set(r.lines.map((l) => l.taxType)).size > 1
  const vatRates = [...new Set(r.vatSummary.filter((v) => v.vat > 0).map((v) => v.rate))]
  return (
    <div className="receipt print-area mx-auto w-[72mm] font-mono text-[11px] leading-snug text-black">
      <div className="text-center">
        <p className="font-sans text-2xl font-bold leading-tight">{r.companyName}</p>
        {r.companyLegalName && r.companyLegalName !== r.companyName && <p className="text-sm font-bold">{r.companyLegalName}</p>}
        <p className="mt-1">{[r.companyAddress, r.locationName].filter(Boolean).join(', ')}</p>
        {r.companyPhone && <p>Shop: {formatPhone(r.companyPhone)}</p>}
        {r.companyKraPin && <p>KRA PIN: {r.companyKraPin}</p>}
        {r.companyVatNumber && <p>VAT No: {r.companyVatNumber}</p>}
        <p className="text-[9px] tracking-wide">{receiptTagline}</p>
      </div>

      <Rule />
      <p className="text-center text-sm font-bold">{receiptTitle(r)}</p>
      <Rule />

      <div>
        <Pair left="Invoice No:" right={r.invoiceCode} />
        {r.orderNumber && <Pair left="Order No:" right={r.orderNumber} />}
        {r.customerOrderNumber && <Pair left="Your LPO:" right={r.customerOrderNumber} />}
        <Pair left="Date:" right={formatDateTime(r.issuedAt)} />
        {r.cashierName && <Pair left="Served by:" right={r.cashierName} />}
        <Pair left="Customer:" right={r.customerName} />
        {r.customerPhone && <Pair left="Phone:" right={formatPhone(r.customerPhone)} />}
        {r.customerKraPin && <Pair left="Customer PIN:" right={r.customerKraPin} />}
      </div>

      <Rule />
      <table className="w-full">
        <thead>
          <tr className="border-b border-dashed border-black font-bold">
            <th className="w-[10%] pb-0.5 text-left">Qty</th>
            <th className="pb-0.5 text-left">Item</th>
            <th className="pb-0.5 text-right">Unit</th>
            <th className="pb-0.5 text-right">Amount</th>
            {mixedRates && <th />}
          </tr>
        </thead>
        <tbody>
          {r.lines.map((l, i) => (
            <tr key={i} className="align-top">
              <td className="pt-0.5">{l.quantity.toLocaleString('en-KE')}</td>
              <td className="pt-0.5 pr-1">
                {l.name}
                {l.discountAmount > 0 && <span className="block">  less discount -{bare(l.discountAmount)}</span>}
              </td>
              <td className="pt-0.5 text-right">{bare(l.unitPrice)}</td>
              <td className="pt-0.5 text-right">{bare((r.pricesIncludeVat ? l.lineTotal : l.lineNet) + l.discountAmount)}</td>
              {mixedRates && <td className="pl-1 pt-0.5">{l.taxType}</td>}
            </tr>
          ))}
        </tbody>
      </table>

      <Rule />
      <div>
        {r.discountTotal > 0 && <Pair left="Discount:" right={`-${formatMoneyDocument(r.discountTotal)}`} />}
        {r.pricesIncludeVat
          ? <Pair left="VAT included:" right={formatMoneyDocument(r.vatTotal)} />
          : (
            <>
              <Pair left="Net Amount:" right={formatMoneyDocument(r.subTotal)} />
              <Pair left={vatRates.length === 1 ? `VAT (${rate(vatRates[0])}):` : 'VAT:'} right={formatMoneyDocument(r.vatTotal)} />
            </>
          )}
        <p className="mt-0.5 flex justify-between text-lg font-bold">
          <span>TOTAL:</span>
          <span>{formatMoneyDocument(r.grandTotal)}</span>
        </p>
      </div>

      {(r.payments.length > 0 || r.changeGiven > 0) && (
        <>
          <Rule />
          <div>
            {r.payments.map((p, i) => (
              <div key={i}>
                <Pair left="Payment Method:" right={tenderLabels[p.mode]} />
                {p.reference && <Pair left={p.mode === 'Mpesa' ? 'Transaction ID:' : 'Reference:'} right={p.reference} />}
                <Pair left="Amount Paid:" right={formatMoneyDocument(p.amount)} />
              </div>
            ))}
            {r.changeGiven > 0 && <Pair left="Change:" right={formatMoneyDocument(r.changeGiven)} />}
          </div>
        </>
      )}

      <Rule />
      <p className="text-sm font-bold">VAT SUMMARY</p>
      <table className="w-full">
        <thead>
          <tr className="text-[10px]"><th className="text-left font-normal">Rate</th><th className="text-right font-normal">Taxable Amount</th><th className="text-right font-normal">VAT</th></tr>
        </thead>
        <tbody>
          {r.vatSummary.map((v) => (
            <tr key={v.rate}><td>{rate(v.rate)}</td><td className="text-right">{bare(v.taxable)}</td><td className="text-right">{bare(v.vat)}</td></tr>
          ))}
        </tbody>
      </table>
      {r.vatWaiverReason && <p>Sold without VAT — {r.vatWaiverReason}</p>}
      {r.vatExemptionCertificate && <p>VAT exempt — certificate {r.vatExemptionCertificate}</p>}

      {/* A SANDBOX STAMP IS NOT A STAMP (D-236).

          eTIMS is pointed at the sandbox, so none of this has been filed with KRA: the CU number
          and signature are invented by a test service, and the receipt said so in bold — "NOT A
          VALID TAX INVOICE", handed to a customer who had just paid. Printing nothing is honest
          and printing a real stamp is honest; printing a convincing fake with a disclaimer under
          it is the one option that helps nobody.

          It comes back the day eTIMS goes live, because by then the number is real and Kenyan law
          wants it on the invoice. */}
      {r.etims && !r.etims.isSandbox && (
        <>
          <Rule />
          <div className="text-center">
            {r.etims.status === 'Transmitted' ? (
              <>
                <p className="text-sm font-bold">KRA eTIMS</p>
                <p>CU Invoice No: {r.etims.invoiceNumber}</p>
                {r.etims.controlUnitId && <p>CU ID: {r.etims.controlUnitId}</p>}
                <p className="break-all">Signature: {r.etims.receiptSignature}</p>
              </>
            ) : (
              <p>eTIMS: being reported to KRA</p>
            )}
          </div>
        </>
      )}

      <Rule />
      <div className="text-center">
        <p className="font-bold">Thank you for shopping with {r.companyName}!</p>
        <p className="text-[9px] tracking-wide">{receiptSignOff}</p>
        {r.orderNumber && <p className="mt-1">Quote {r.orderNumber} for any query.</p>}
        {r.paymentInstructions && <p className="mt-1 font-bold">{r.paymentInstructions}</p>}
        {r.footerText && <p className="mt-1">{r.footerText}</p>}
        {!r.customerIsWalkIn && (
          // The Data Protection Act notice: what the number is for, and what it is not.
          <p className="mt-1">
            Your number is used for this receipt, deliveries and warranty reminders. Promotions only if you opt in.
          </p>
        )}
      </div>
    </div>
  )
}

function Rule() {
  return <div className="my-1.5 border-t border-dashed border-black" aria-hidden="true" />
}

function Pair({ left, right }: { left: string; right: string }) {
  return (
    <p className="flex justify-between gap-2">
      <span className="min-w-0">{left}</span>
      <span className="shrink-0 text-right">{right}</span>
    </p>
  )
}
